import { Alert, Box, Container, Fade, FormHelperText, Paper, Snackbar, Typography } from '@mui/material';
import { useEffect, useState, FC } from 'react';
import { useNavigate } from 'react-router';
import PaymentVendors from '@acorn-portal/shared/src/components/vendor/PaymentVendors';
import SimpleBraintreePaymentForm from '../../components/payment/simple-braintree-payment-form';
import PaymentHeader from '@acorn-portal/shared/src/components/header/PaymentHeader';
import { formatCurrency } from '@acorn-portal/shared/src/utils/formatter';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { useDispatch } from 'react-redux';
import { updatePayNowDetails } from '@acorn-portal/shared/src/store/paynow.slice';
import { PayNowClient } from '@acorn-portal/shared/src/services/api';
import API from '@acorn-portal/shared/src/services/AxiosInstance';
import { CustomPaymentMethodPayload } from '@acorn-portal/shared/src/utils/types/extensions/braintree';
import LoadingModal from '@acorn-portal/shared/src/components/modal/LoadingModal';

const ProcessPayment: FC = () => {
	const client = new PayNowClient(undefined, API);
	const dispatch = useDispatch();
	const payNowDetails = useAppSelector((redux) => redux.payNow);
	const [errorMessage, setErrorMessage] = useState<string>();
	const [openError, setError] = useState(false);
	const [loading, setLoading] = useState(false);
	const [clientToken, setClientToken] = useState('');
	const [amount, setAmountToPay] = useState(0);
	const [submitting, setSubmitting] = useState(false);
	const navigate = useNavigate();

	const { collectionOptions, missingPaymentFormDetails, details } = payNowDetails;

	const isVaulting = missingPaymentFormDetails ? !missingPaymentFormDetails.vaultCardOptOut : false;

	const getClientToken = async () => {
		setLoading(true);
		try {
			const response = await client.generatePaymentToken(undefined);
			setClientToken(response);
		} catch (e) {
			console.error('Error getting client token:', e);
			setErrorMessage('Unable to get payment token. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	const onProcessPayment = async (payload: CustomPaymentMethodPayload) => {
		if (!details) {
			setError(true);
			return;
		}

		const { nonce, deviceData, threeDSecureInfo } = payload;

		try {
			const response = await client.makePayment({
				paymentNonce: nonce,
				amount,
				threeDSecureAuthenticationId: threeDSecureInfo ? threeDSecureInfo.threeDSecureAuthenticationId : '',
				emailAddress: missingPaymentFormDetails ? missingPaymentFormDetails.email : '',
				deviceData,
				directDebitId: details.missedPaymentDetails?.directDebitId,
				paymentPlanType: missingPaymentFormDetails.splitPaymentPlanType,
				vaultCard: isVaulting,
			});

			if (response.isSuccessful) {
				const paymentDetails = await client.getPaymentDetails(details.policyReference);
				dispatch(updatePayNowDetails({ details: paymentDetails }));
				navigate(ROUTE_PATHS.payNowConfirmation);
			} else {
				setError(true);
			}
		} catch (e) {
			console.error('Error processing payment:', e);
			setError(true);
		}
	};

	useEffect(() => {
		if (!payNowDetails.details) {
			navigate(ROUTE_PATHS.home);
			return;
		}

		if (clientToken === '') {
			getClientToken().catch(console.error);
		}

		let amountToPay = 0;
		if (missingPaymentFormDetails.proceed) {
			amountToPay = collectionOptions.find((x) => x.paymentPlanType === missingPaymentFormDetails.splitPaymentPlanType)
				?.installments[0].amount as number;
		} else {
			amountToPay = payNowDetails?.details.overDuePaymentPlan?.amount ?? 0;
		}

		if (amountToPay === 0) navigate(ROUTE_PATHS.error);

		setAmountToPay(amountToPay);
	}, [payNowDetails]);

	return (
		<Paper
			elevation={1}
			sx={{
				margin: 1.5,
				borderRadius: '8px',
				paddingBottom: 3,
			}}
		>
			<Fade in>
				<Container maxWidth='sm'>
					<Box sx={{ padding: 1 }} />
					<Box my={4}>
						<PaymentHeader title='Secure Payment Checkout' />
						<Typography textAlign='left' sx={{ fontSize: '20px' }}>
							Payment to be taken : <b>{amount ? formatCurrency(amount) : ''}</b>
						</Typography>
						<Typography textAlign='left'>You will receive an email as receipt and confirmation of payment.</Typography>
						<SimpleBraintreePaymentForm
							loading={loading}
							amount={amount?.toString()}
							clientToken={clientToken}
							submitPayment={onProcessPayment}
							isOptOut={!isVaulting}
							onSubmitting={(value) => setSubmitting(value)}
						/>

						{errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
						<PaymentVendors />
					</Box>
					<Snackbar open={openError} autoHideDuration={6000} onClose={() => setError(false)}>
						<Alert onClose={() => setError(false)} severity='error' sx={{ width: '100%' }}>
							Unable to process payment.
						</Alert>
					</Snackbar>
				</Container>
			</Fade>
			<LoadingModal open={submitting} message='Processing Payment' />
		</Paper>
	);
};
export default ProcessPayment;
